import { Link } from "react-router-dom";
import { Link2, Palette, MessageCircle, CalendarDays, PanelsTopLeft, Lock, KeyRound } from "lucide-react";
import { TdvPricing } from "@/threedvista/TdvPricing";
import { MissingList } from "@/threedvista/G360Connect";

const FEATURES = [
  [Link2, "Paste your tour link", "Add the link of any Giraffe360 tour. Nothing to download or upload."],
  [Palette, "Your brand around the tour", "Your logo, colours, start screen and top menu in the skin you pick."],
  [PanelsTopLeft, "Menus and info windows", "Link buttons, dropdowns, info pages, floor plans and contact details."],
  [MessageCircle, "An AI assistant", "Answers visitor questions from your FAQs and PDFs, day and night."],
  [CalendarDays, "Viewing requests", "Visitors book a viewing and you get the lead straight away."],
  [Lock, "Password and stats", "Protect a tour with per-person logins and see who viewed what."],
];

export default function GiraffeHome() {
  const signup = "/giraffe/signup";
  return (
    <div className="min-h-screen bg-[#0B1220] text-white" data-testid="g360-home">
      <header className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/giraffe" className="font-serif text-xl" data-testid="g360-home-logo">Showhome <span className="text-[#C9A227] hidden sm:inline">for Giraffe360</span></Link>
        <div className="flex items-center gap-1 sm:gap-2">
          <a href="#pricing" className="hidden sm:grid h-10 px-4 rounded-full text-sm font-semibold hover:bg-white/10 place-items-center" data-testid="g360-home-pricing">Pricing</a>
          <Link to="/giraffe/login" className="h-10 px-4 rounded-full text-sm font-semibold hover:bg-white/10 grid place-items-center" data-testid="g360-home-login">Log in</Link>
          <Link to={signup} className="h-10 px-5 rounded-full bg-[#C9A227] text-slate-950 text-sm font-semibold grid place-items-center" data-testid="g360-home-signup">Sign up</Link>
        </div>
      </header>
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-24">
        <p className="text-xs uppercase tracking-[0.3em] text-[#C9A227] rise">For Giraffe360 tours</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl max-w-3xl mt-5 leading-[1.05] rise">Turn your Giraffe360 tour into an interactive selling experience.</h1>
        <p className="text-base md:text-lg text-white/70 max-w-2xl mt-6 rise">Paste the tour link once and add your branding, menus, info windows, an AI assistant and viewing requests around it.</p>
        <div className="flex flex-wrap gap-3 mt-10 rise">
          <Link to="/giraffe/try" className="h-12 px-7 rounded-full bg-[#C9A227] text-slate-950 font-semibold grid place-items-center hover:opacity-90 transition-opacity" data-testid="g360-home-try">Try it on your own tour</Link>
          <Link to={signup} className="h-12 px-7 rounded-full border border-white/20 font-semibold grid place-items-center hover:bg-white/10 transition-colors" data-testid="g360-home-cta">Create your account</Link>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-6 pb-28 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {FEATURES.map(([Icon, t, d], i) => (
          <div key={t} className="rounded-3xl border border-white/10 bg-white/[0.03] p-7 rise" style={{ animationDelay: `${i * 60}ms` }} data-testid={`g360-feature-${i}`}>
            <Icon className="w-6 h-6 text-[#C9A227]" />
            <p className="font-semibold text-lg mt-5">{t}</p>
            <p className="text-sm text-white/60 mt-2">{d}</p>
          </div>
        ))}
      </section>
      <section className="max-w-6xl mx-auto px-6 pb-28" data-testid="g360-home-key">
        <div className="rounded-3xl border border-[#C9A227]/40 bg-[#C9A227]/[0.06] p-8 grid md:grid-cols-[1fr_1.2fr] gap-8">
          <div>
            <KeyRound className="w-6 h-6 text-[#C9A227]" />
            <p className="font-serif text-2xl mt-4">Even more with your Giraffe360 API key</p>
            <p className="text-sm text-white/60 mt-2">Add your Giraffe360 API key in your account and we show your rooms in our own 360° viewer. Without a key, these features are missing:</p>
          </div>
          <MissingList dark />
        </div>
      </section>
      <TdvPricing currency="USD" signup={signup} />
    </div>
  );
}
